import React from 'react'
import { isEmpty, isNil } from 'lodash'
import { IconAlertTriangle } from '@tabler/icons-react'

import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'

const GB = 1e9

export const libraryLabelOf = (library) =>
  `${library.library_name ?? 'A library'} in ${library.site_path ?? 'an unknown site'}`

// Older agent-gpt sends no backfill; none is what a missing key meant there.
export const heldLibraries = (syncState) => (syncState?.backfill ?? []).filter((lib) => lib.held)

const selectedSize = (library) =>
  isNil(library.included_bytes) ? '' : ` (${(library.included_bytes / GB).toFixed(1)} GB)`

/**
 * One warning per library too large to sync until it is narrowed or Balto approves it
 * (RTG3-1300). Nothing from a held library reaches AI Answers, so without this the
 * library would just look like it never syncs.
 *
 * @param {Object} props.syncState undefined | null | the connection's ingest state
 * @param {boolean} props.canApprove the viewer is Balto staff, who may approve a held library
 * @param {Function} props.onApprove library => void, asks to approve it
 */
export const HeldLibraryBanners = ({ syncState, canApprove = false, onApprove }) => {
  const held = heldLibraries(syncState)
  if (isEmpty(held)) {
    return null
  }
  // Sent alongside backfill, so present whenever a library is held.
  const limits = syncState.hold_limits
  return (
    <div className="flex-column small-gap mbs" data-testid="sharepoint-held-libraries">
      {held.map((library) => (
        <Banner
          key={library.drive_id}
          warning
          icon={<IconAlertTriangle />}
          data-testid={`sharepoint-held-library-${library.drive_id}`}
        >
          <div className="flex-column small-gap">
            <strong>{libraryLabelOf(library)} is too large to sync</strong>
            <p>
              It has {library.included_files.toLocaleString()} files to index
              {selectedSize(library)}, over the limit of {limits.files.toLocaleString()} files or{' '}
              {limits.gb} GB for one library, so we’ve paused adding it to AI Answers. Contact Balto
              support to narrow it or approve it, or remove its row below.
            </p>
            {canApprove && (
              <div>
                <Button xs onClick={() => onApprove(library)}>
                  Approve this size
                </Button>
              </div>
            )}
          </div>
        </Banner>
      ))}
    </div>
  )
}
